import { useState, useEffect, useRef, useCallback } from 'react';
import { Send, X, Loader2 } from 'lucide-react';
import axios from 'axios';
import MarkdownMessage from './MarkdownMessage';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';
const ROOTY_IMG = 'https://customer-assets.emergentagent.com/job_5adcc4e8-1bb2-4cdf-8c44-5c6c1ccd2118/artifacts/1skt1b81_IMG_1754.png';

const QUICK_PROMPTS = [
  "Sales this week vs last week",
  "Which store needs the most attention?",
  "Summarize today's audits"
];

export default function FloatingRooty() {
  const [open, setOpen] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const endRef = useRef(null);

  useEffect(() => {
    if (open) endRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, open]);

  const ensureSession = async () => {
    if (sessionId) return sessionId;
    try {
      const res = await axios.post(`${API_URL}/ai/sessions`);
      setSessionId(res.data.id);
      return res.data.id;
    } catch { return null; }
  };

  const send = async (textOverride) => {
    const text = (textOverride || input).trim();
    if (!text || loading) return;
    setInput('');
    const sid = await ensureSession();
    if (!sid) return;
    const userMsg = { id: 't-' + Date.now(), role: 'user', content: text };
    setMessages(prev => [...prev, userMsg]);
    setLoading(true);
    try {
      const res = await axios.post(`${API_URL}/ai/chat`, {
        session_id: sid, message: text, model: 'gemini'
      });
      setMessages(prev => [...prev, { id: res.data.message_id, role: 'assistant', content: res.data.response }]);
    } catch (e) {
      setMessages(prev => [...prev, { id: 'err-' + Date.now(), role: 'assistant', content: 'Sorry, I hit a snag. Try again in a moment.' }]);
    } finally {
      setLoading(false);
    }
  };

  // (legacy fmt removed — now handled by <MarkdownMessage />)

  return (
    <>
      {/* Floating button */}
      {!open && (
        <button
          onClick={() => setOpen(true)}
          className="fixed bottom-24 right-4 lg:bottom-6 lg:right-6 z-50 w-16 h-16 rounded-full bg-white shadow-xl shadow-orange-300/60 ring-2 ring-orange-500 flex items-center justify-center overflow-hidden hover:scale-110 transition-transform active:scale-95"
          data-testid="floating-rooty-button"
          title="Ask Rooty"
        >
          <img src={ROOTY_IMG} alt="Rooty" className="w-14 h-14 object-contain" />
          <span className="absolute -top-0.5 -right-0.5 w-3.5 h-3.5 bg-red-500 rounded-full border-2 border-white animate-pulse" />
        </button>
      )}

      {/* Chat panel */}
      {open && (
        <div className="fixed bottom-20 right-2 lg:bottom-6 lg:right-6 z-50 w-[calc(100vw-1rem)] max-w-[380px] h-[560px] bg-white rounded-2xl shadow-2xl border border-slate-200 flex flex-col overflow-hidden" data-testid="floating-rooty-panel">
          {/* Header */}
          <div className="shrink-0 bg-gradient-to-r from-orange-500 to-amber-500 px-4 py-3 flex items-center justify-between text-white">
            <div className="flex items-center gap-2">
              <div className="w-10 h-10 rounded-full bg-white/95 flex items-center justify-center overflow-hidden ring-2 ring-white/60">
                <img src={ROOTY_IMG} alt="Rooty" className="w-9 h-9 object-contain" />
              </div>
              <div>
                <h3 className="font-bold text-sm leading-tight">Rooty</h3>
                <p className="text-[10px] opacity-80 leading-tight">A&W AI Assistant</p>
              </div>
            </div>
            <button onClick={() => setOpen(false)} className="hover:bg-white/20 rounded-lg p-1" data-testid="floating-rooty-close">
              <X className="w-4 h-4" />
            </button>
          </div>

          {/* Messages */}
          <div className="flex-1 overflow-y-auto px-3 py-3 space-y-3 bg-slate-50">
            {messages.length === 0 && !loading && (
              <div className="text-center pt-6">
                <div className="w-20 h-20 rounded-full bg-gradient-to-br from-orange-50 to-amber-100 mx-auto flex items-center justify-center mb-2 overflow-hidden ring-2 ring-orange-200">
                  <img src={ROOTY_IMG} alt="Rooty" className="w-18 h-18 object-contain" />
                </div>
                <p className="text-sm font-semibold text-slate-800">Hi, I'm Rooty 👋</p>
                <p className="text-xs text-slate-500 mt-1 px-4">Your A&W AI assistant. Ask me about sales, audits, KPIs, or anything operational.</p>
                <div className="mt-4 space-y-1.5">
                  {QUICK_PROMPTS.map((p, i) => (
                    <button
                      key={i}
                      onClick={() => send(p)}
                      className="block w-full text-left text-xs px-3 py-2 bg-white border border-slate-200 rounded-lg hover:border-orange-300 text-slate-600 hover:text-slate-800"
                      data-testid={`rooty-quick-${i}`}
                    >{p}</button>
                  ))}
                </div>
              </div>
            )}
            {messages.map(m => (
              <div key={m.id} className={`flex ${m.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                <div className={`max-w-[85%] text-xs leading-relaxed px-3 py-2 rounded-2xl ${m.role === 'user' ? 'bg-orange-500 text-white' : 'bg-white border border-slate-200 text-slate-700'}`}>
                  {m.role === 'user' ? m.content : <MarkdownMessage>{m.content}</MarkdownMessage>}
                </div>
              </div>
            ))}
            {loading && (
              <div className="flex justify-start">
                <div className="bg-white border border-slate-200 rounded-2xl px-3 py-2 text-xs text-slate-400 flex items-center gap-2">
                  <Loader2 className="w-3 h-3 animate-spin" /> Rooty is thinking...
                </div>
              </div>
            )}
            <div ref={endRef} />
          </div>

          {/* Input */}
          <div className="shrink-0 border-t border-slate-200 bg-white px-2 py-2 flex gap-2">
            <input
              value={input}
              onChange={e => setInput(e.target.value)}
              onKeyDown={e => { if (e.key === 'Enter') send(); }}
              placeholder="Ask Rooty anything..."
              className="flex-1 text-xs px-3 py-2 rounded-lg border border-slate-200 focus:border-orange-300 focus:outline-none"
              disabled={loading}
              data-testid="floating-rooty-input"
            />
            <button
              onClick={() => send()}
              disabled={!input.trim() || loading}
              className="bg-orange-500 hover:bg-orange-600 disabled:opacity-50 text-white rounded-lg px-3"
              data-testid="floating-rooty-send"
            >
              <Send className="w-4 h-4" />
            </button>
          </div>
        </div>
      )}
    </>
  );
}
